import { useState } from 'react';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { AiKeyMissingNotice } from './AiKeyBanner.jsx';
import { Modal } from './ui';

// T028 — Manual job paste modal.
//
// Operator finds a job posting somewhere outside hunter (LinkedIn, a
// recruiter email, a careers page no adapter covers) and wants it
// scored + tracked. Two input modes:
//
//   "URL"  → server fetches the page; server-side fetch is best-effort
//            (anti-bot, paywalls, JS-rendered pages all fail). When the
//            fetch fails the UI nudges the operator to paste the text
//            directly.
//   "Text" → operator pastes the posting content from clipboard. Most
//            reliable, especially for LinkedIn-derived listings where
//            we can't legally scrape.
//
// On success the new recommendation is auto-shortlisted (manual paste =
// explicit interest signal) and the dashboard refreshes to surface it.

export default function ManualJobModal({ onClose, onCreated }) {
  const [mode, setMode] = useState('text'); // 'url' | 'text'
  const [url, setUrl] = useState('');
  const [rawText, setRawText] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null); // T127 — extracted job, awaiting confirmation
  const { user } = useAuth();
  const hasAnthropicKey = !!user?.connectors?.anthropicKey;

  const submit = async () => {
    const body = {};
    if (mode === 'url') {
      if (!url.trim()) {
        toast('Paste a URL first', { kind: 'info' });
        return;
      }
      body.url = url.trim();
    } else {
      if (rawText.trim().length < 50) {
        toast('Paste at least the title + first paragraph of the posting (50+ chars)', { kind: 'info' });
        return;
      }
      body.rawText = rawText;
      if (sourceUrl.trim()) body.sourceUrl = sourceUrl.trim();
    }
    setSubmitting(true);
    try {
      const r = await api.post('/jobs/manual', body);
      setPreview(r); // T127 — hold for the confirmation card; the rec is already created + shortlisted server-side
    } catch (e) {
      const code = e.body?.error;
      let msg = e.body?.message || e.message;
      if (code === 'manual_fetch_failed' || code === 'manual_fetch_timeout' || code === 'manual_fetch_too_large') {
        msg = `Couldn't fetch that URL (${code.replace('manual_fetch_', '')}). Try the "Text" tab and paste the posting content directly.`;
      } else if (code === 'anthropic_unauthorized') {
        msg = 'Set your Anthropic API key in Settings → Connectors first.';
      } else if (code === 'manual_extraction_invalid') {
        msg = 'Claude couldn\'t pull a clear job title + company out of that text. Paste a bit more of the posting.';
      }
      toast(msg, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // T127 — the rec was created on submit; confirm just refreshes + closes,
  // discard returns to the form (inputs intact, no second Claude call).
  const confirmPreview = () => {
    toast(`Tracking: ${preview.job.title} @ ${preview.job.company} (score ${preview.recommendation.scoreTotal})`, {
      kind: 'success',
    });
    onCreated?.(preview);
    onClose?.();
  };
  const discardPreview = () => setPreview(null);

  return (
    <Modal role="dialog" size="md" scroll labelledById="manual-job-title" onClose={onClose}>
        <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-brand-500">Add manually</p>
            <h2 id="manual-job-title" className="mt-0.5 text-base font-semibold text-slate-100">
              Score an outside job posting
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Paste a URL or the posting text — Claude extracts the fields, the rules scorer rates it, and it lands in
              your Shortlisted column ready to track.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
          >
            ✕
          </button>
        </header>

        {!preview && (
          <div className="flex border-b border-slate-800 px-2">
            <TabButton active={mode === 'text'} onClick={() => setMode('text')}>
              Paste text
            </TabButton>
            <TabButton active={mode === 'url'} onClick={() => setMode('url')}>
              URL (server-fetched)
            </TabButton>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {!preview && !hasAnthropicKey && (
            <div className="mb-4">
              <AiKeyMissingNotice feature="Manual job scoring" />
            </div>
          )}
          {preview ? (
            <ExtractionPreview data={preview} />
          ) : mode === 'text' ? (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400">
                  Posting content
                </label>
                <textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  rows={14}
                  placeholder="Paste the whole posting from your clipboard — title, company, location, what they do, what they need, what they offer. Claude will extract the structured fields."
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm leading-relaxed text-slate-100 focus:border-brand-500 focus:outline-none"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {rawText.length} characters · capped at 60000 server-side
                </p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400">
                  Source URL (optional)
                </label>
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://… — where you found this posting. Just for reference."
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400">
                  Job posting URL
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.dk/careers/senior-engineer"
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
                />
              </div>
              <p className="rounded-lg border border-amber-700 bg-amber-950/20 p-2.5 text-xs text-amber-200">
                ⚠ Server-side fetch is best-effort: LinkedIn, paywalled boards, and JS-rendered pages will fail. Use the{' '}
                <button
                  type="button"
                  onClick={() => setMode('text')}
                  className="underline decoration-dotted hover:text-amber-100"
                >
                  Paste text
                </button>{' '}
                tab if the URL doesn't load.
              </p>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
          {preview ? (
            <>
              <button
                type="button"
                onClick={discardPreview}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-400 hover:border-slate-500 hover:text-slate-200"
              >
                ↺ Discard & re-paste
              </button>
              <button
                type="button"
                onClick={confirmPreview}
                className="rounded-md bg-brand-600 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-500"
              >
                Looks right — track it →
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-400 hover:border-slate-500 hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              {hasAnthropicKey && (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  className="rounded-md bg-brand-600 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-500 disabled:opacity-50"
                >
                  {submitting ? 'extracting…' : '✨ Add + score'}
                </button>
              )}
            </>
          )}
        </footer>
    </Modal>
  );
}

// T127 — confirmation card shown after extraction, before tracking. Mirrors the
// CV-parse review block: emerald surface, title/company/location, score chip,
// top reasons/risks. The rec already exists server-side (shortlisted) — this is
// a "did we get the right job?" gate before refreshing the dashboard.
function ExtractionPreview({ data }) {
  const { job, recommendation: rec, tokensIn, tokensOut } = data;
  const loc = [job.location?.city, job.location?.country].filter(Boolean).join(', ');
  const remote = job.location?.remote && job.location.remote !== 'unknown' ? job.location.remote : null;
  return (
    <div className="space-y-3 rounded-lg border border-emerald-800 bg-emerald-950/20 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-emerald-300">Extracted ✓ — confirm before tracking</p>
          <h3 className="mt-1 truncate text-base font-semibold text-slate-100">{job.title}</h3>
          <p className="truncate text-sm text-emerald-200">
            @ {job.company}
            {loc && <span className="text-emerald-300/70"> · {loc}</span>}
            {remote && <span className="text-emerald-300/70"> · {remote}</span>}
          </p>
        </div>
        <span
          className="shrink-0 rounded border border-emerald-700/60 px-2 py-0.5 font-mono text-xs text-emerald-300"
          title="Rules score"
        >
          {rec.scoreTotal}
        </span>
      </div>
      {job.extracted?.skills?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {job.extracted.skills.slice(0, 12).map((s) => (
            <span
              key={s}
              className="rounded-full border border-emerald-700/60 bg-emerald-950/30 px-2 py-0.5 text-[10px] text-emerald-200"
            >
              {s}
            </span>
          ))}
        </div>
      )}
      {rec.reasons?.length > 0 && (
        <ul className="space-y-0.5 text-xs text-emerald-100">
          {rec.reasons.slice(0, 4).map((x, i) => (
            <li key={i} className="flex items-start gap-1">
              <span className="shrink-0" aria-hidden="true">✓</span>
              <span className="sr-only">Reason: </span>
              <span className="leading-snug">{x}</span>
            </li>
          ))}
        </ul>
      )}
      {rec.risks?.length > 0 && (
        <ul className="space-y-0.5 text-xs text-amber-200">
          {rec.risks.slice(0, 3).map((x, i) => (
            <li key={i} className="flex items-start gap-1">
              <span className="shrink-0" aria-hidden="true">⚠</span>
              <span className="sr-only">Risk: </span>
              <span className="leading-snug">{x}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-emerald-400/70">
        Landed in your <strong>Shortlisted</strong> column. Wrong company or title? Discard and paste more of the posting.
        {tokensIn || tokensOut ? ` · ${(tokensIn ?? 0) + (tokensOut ?? 0)} tokens` : ''}
      </p>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-3 py-2 text-xs transition ' +
        (active
          ? 'border-b-2 border-brand-500 text-brand-400'
          : 'border-b-2 border-transparent text-slate-400 hover:text-slate-200')
      }
    >
      {children}
    </button>
  );
}
